import { useState } from "react"
import { useTranslation } from "@/clientWeb/i18n/LanguageContext"
import { useNavigate, useParams } from "react-router-dom"
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell
} from "recharts"
import { ChevronDown, ChevronUp, ClipboardList } from "lucide-react"

const COLORS = ["#0ea5e9", "#8b5cf6", "#f97316", "#10b981", "#ec4899", "#f59e0b"]

interface Poll {
  id: string
  question: string
  submittedUsers: number
  options: { label: string; votes: number }[]
}

function CustomTooltip({ active, payload, label }: any) {
  if (!active || !payload?.length) return null
  return (
    <div className="bg-card dark:bg-card border border-stroke-100 dark:border-border dark:border-border rounded-xl shadow-lg p-3 text-xs">
      <p className="font-semibold text-slate-800 dark:text-white mb-1">{label}</p>
      {payload.map((p: any, i: number) => (
        <p key={i} style={{ color: p.fill }} className="mt-0.5">{p.name}: <strong>{p.value}</strong></p>
      ))}
    </div>
  )
}

export default function WebinarPolls() {
  const t = useTranslation()
  const navigate = useNavigate()
  const { id }   = useParams()
  const [open, setOpen] = useState<string[]>(["p1"])

  const mockPolls: Poll[] = [
    {
      id: "p1",
      question: t("webinar.pollQuestion1"),
      submittedUsers: 52,
      options: [
        { label: t("webinar.pollOption1_1"), votes: 22 },
        { label: t("webinar.pollOption1_2"), votes: 15 },
        { label: t("webinar.pollOption1_3"), votes: 10 },
        { label: t("webinar.pollOption1_4"), votes: 5  },
      ],
    },
    {
      id: "p2",
      question: t("webinar.pollQuestion2"),
      submittedUsers: 56,
      options: [
        { label: t("webinar.pollOption2_1"), votes: 18 },
        { label: t("webinar.pollOption2_2"), votes: 24 },
        { label: t("webinar.pollOption2_3"), votes: 10 },
        { label: t("webinar.pollOption2_4"), votes: 4  },
      ],
    },
    {
      id: "p3",
      question: t("webinar.pollQuestion3"),
      submittedUsers: 48,
      options: [
        { label: t("webinar.pollOption3_1"), votes: 20 },
        { label: t("webinar.pollOption3_2"), votes: 16 },
        { label: t("webinar.pollOption3_3"), votes: 8  },
        { label: t("webinar.pollOption3_4"), votes: 4  },
      ],
    },
    {
      id: "p4",
      question: t("webinar.pollQuestion4"),
      submittedUsers: 54,
      options: [
        { label: t("webinar.pollOption4_1"), votes: 38 },
        { label: t("webinar.pollOption4_2"), votes: 12 },
        { label: t("webinar.pollOption4_3"), votes: 4  },
      ],
    },
  ]

  const togglePoll = (pid: string) =>
    setOpen(prev => prev.includes(pid) ? prev.filter(x => x !== pid) : [...prev, pid])

  const totalResponses = mockPolls.reduce((s, p) => s + p.submittedUsers, 0)

  return (
    <div className="flex flex-col h-full w-full overflow-y-auto bg-background p-4 md:p-6 gap-5">

      {/* Breadcrumb + Header */}
      <div>
        <div className="flex items-center gap-2 text-sm text-slate-400 mb-3">
          <button onClick={() => navigate("/webinar")} className="text-primary font-semibold hover:text-primary transition-colors">{t("webinar.webinar")}</button>
          <span>/</span>
          <button onClick={() => navigate(`/webinar/${id}/analytics`)} className="text-primary font-semibold hover:text-primary transition-colors">{t("webinar.analytics")}</button>
          <span>/</span>
          <span className="text-slate-700 dark:text-slate-200 font-semibold">{t("webinar.polls")}</span>
        </div>

        <div className="flex items-center gap-3">
          {/* <button
            onClick={() => navigate(`/webinar/${id}/analytics`)}
            className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors"
          >
            <ArrowLeft className="w-4 h-4 text-slate-600 dark:text-slate-400" />
          </button> */}
          <div>
            <h1 className="text-xl font-bold text-slate-900 dark:text-foreground/80 dark:text-white">{t("webinar.pollResults")}</h1>
            <p className="text-xs text-slate-400 mt-0.5">
              {mockPolls.length} {t("webinar.pollsLabel")} · {totalResponses} {t("webinar.totalResponses")}
            </p>
          </div>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-3 gap-3">
        {[
          { label: t("webinar.pollsCreated"),   value: mockPolls.length, iconBg: "bg-gradient-to-br from-blue-50 to-indigo-100 dark:from-blue-900/20 dark:to-indigo-900/20" },
          { label: t("webinar.totalResponses"), value: totalResponses, iconBg: "bg-gradient-to-br from-emerald-50 to-teal-100 dark:from-emerald-900/20 dark:to-teal-900/20" },
          { label: t("webinar.avgResponses"),  value: Math.round(totalResponses / mockPolls.length), iconBg: "bg-gradient-to-br from-amber-50 to-orange-100 dark:from-amber-900/20 dark:to-orange-900/20" },
        ].map(s => (
          <div key={s.label} className="group relative bg-card dark:bg-card border border-stroke-100 dark:border-border dark:border-border rounded-2xl px-4 py-4 shadow-xs overflow-hidden text-left">
            <div className={`absolute inset-0 ${s.iconBg}`}>
              <svg className="absolute bottom-0 right-0 w-full h-full" viewBox="0 0 200 120" fill="none" preserveAspectRatio="none">
                <path d="M200 120V80C180 70 160 75 140 80C120 85 100 90 80 85C60 80 40 70 20 75C10 77.5 5 79 0 80V120H200Z" fill="currentColor" className="text-white/30 dark:text-white/10" />
              </svg>
            </div>
            <div className="relative z-10">
              <div className="text-xl font-bold text-slate-900 dark:text-foreground/80 dark:text-white mb-1">{s.value}</div>
              <div className="text-sm font-medium text-slate-600 dark:text-slate-400">{s.label}</div>
            </div>
          </div>
        ))}
      </div>

      {/* No polls state */}
      {mockPolls.length === 0 && (
        <div className="flex flex-col items-center justify-center py-20 gap-3">
          <ClipboardList className="w-16 h-16 text-slate-200 dark:text-slate-700" />
          <p className="text-sm font-semibold text-slate-500">{t("webinar.noPollsCreated")}</p>
        </div>
      )}

      {/* Poll Cards */}
      <div className="space-y-3">
        {mockPolls.map((poll, idx) => {
          const total = poll.options.reduce((s, o) => s + o.votes, 0)
          return (
            <div key={poll.id} className="bg-card dark:bg-card border border-stroke-100 dark:border-border dark:border-border rounded-2xl overflow-hidden shadow-sm">
              <button
                onClick={() => togglePoll(poll.id)}
                className="w-full flex items-center gap-3 px-5 py-4 hover:bg-slate-50 dark:hover:bg-slate-700/30 transition-colors text-left"
              >
                <div className="w-7 h-7 rounded-full bg-indigo-500 flex items-center justify-center text-white text-xs font-bold shrink-0">
                  Q{idx + 1}
                </div>
                <span className="flex-1 text-sm font-semibold text-slate-800 dark:text-slate-200">{poll.question}</span>
                <div className="flex items-center gap-3 shrink-0">
                  <span className="text-xs text-slate-400">{poll.submittedUsers} {t("webinar.usersLabel")}</span>
                  {open.includes(poll.id)
                    ? <ChevronUp   className="w-4 h-4 text-slate-400" />
                    : <ChevronDown className="w-4 h-4 text-slate-400" />}
                </div>
              </button>

              {open.includes(poll.id) && (
                <div className="px-5 pb-5 border-t border-stroke-100 dark:border-border dark:border-border">
                  {/* Bar chart */}
                  <div className="h-[150px] mt-4">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={poll.options} margin={{ top: 8, right: 8, left: -20, bottom: 0 }} barSize={32}>
                        <CartesianGrid strokeDasharray="3 3" stroke="rgba(148,163,184,0.2)" />
                        <XAxis dataKey="label" fontSize={10} stroke="currentColor" />
                        <YAxis fontSize={10} stroke="currentColor" allowDecimals={false} />
                        <Tooltip content={<CustomTooltip />} />
                        <Bar dataKey="votes" name="Votes" radius={[4, 4, 0, 0]}>
                          {poll.options.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </div>

                  {/* Legend + percentage */}
                  <div className="space-y-2 mt-3">
                    {poll.options.map((o, i) => {
                      const pct = total > 0 ? ((o.votes / total) * 100).toFixed(0) : "0"
                      return (
                        <div key={o.label} className="flex items-center gap-2">
                          <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: COLORS[i % COLORS.length] }} />
                          <span className="text-xs text-slate-600 dark:text-slate-400 flex-1">{o.label}</span>
                          <div className="flex-1 h-1.5 bg-slate-100 dark:bg-slate-700 rounded-full overflow-hidden max-w-[100px]">
                            <div
                              className="h-full rounded-full transition-all"
                              style={{ width: `${pct}%`, backgroundColor: COLORS[i % COLORS.length] }}
                            />
                          </div>
                          <span className="text-xs font-bold text-slate-700 dark:text-white w-8 text-right">{pct}%</span>
                          <span className="text-xs text-slate-400 w-10 text-right">{o.votes} {t("webinar.votesLabel")}</span>
                        </div>
                      )
                    })}
                  </div>
                </div>
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}
